import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';

interface SnackbarContextType {
  showSnackbar: (message: string, severity?: AlertColor) => void;
  showError: (message: string) => void;
  showSuccess: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};

export type SnackbarAnchorOrigin = {
  vertical: 'top' | 'bottom';
  horizontal: 'left' | 'center' | 'right';
};

interface SnackbarProviderProps {
  children: ReactNode;
  /** Position of the snackbar. Default: bottom-right */
  anchorOrigin?: SnackbarAnchorOrigin;
}

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({
  children,
  anchorOrigin = { vertical: 'bottom', horizontal: 'right' },
}) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<AlertColor>('error');

  const showSnackbar = useCallback((msg: string, sev: AlertColor = 'error') => {
    setMessage(msg);
    setSeverity(sev);
    setOpen(true);
  }, []);

  const showError = useCallback((msg: string) => {
    showSnackbar(msg, 'error');
  }, [showSnackbar]);

  const showSuccess = useCallback((msg: string) => {
    showSnackbar(msg, 'success');
  }, [showSnackbar]);

  const showWarning = useCallback((msg: string) => {
    showSnackbar(msg, 'warning');
  }, [showSnackbar]);

  const showInfo = useCallback((msg: string) => {
    showSnackbar(msg, 'info');
  }, [showSnackbar]);

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const snackbarMaxWidth = { xs: 'calc(100vw - 32px)', sm: 560 };

  return (
    <SnackbarContext.Provider
      value={{
        showSnackbar,
        showError,
        showSuccess,
        showWarning,
        showInfo,
      }}
    >
      {children}
      <Snackbar
        open={open}
        autoHideDuration={30000}
        onClose={handleClose}
        anchorOrigin={anchorOrigin}
        sx={{
          zIndex: (theme) => theme.zIndex.snackbar,
          ...(anchorOrigin.vertical === 'top'
            ? {
                top: {
                  xs: 'calc(8px + env(safe-area-inset-top, 0px))',
                  sm: 'calc(24px + env(safe-area-inset-top, 0px))',
                },
              }
            : {
                bottom: {
                  xs: 'calc(8px + env(safe-area-inset-bottom, 0px))',
                  sm: 'calc(24px + env(safe-area-inset-bottom, 0px))',
                },
              }),
        }}
      >
        <Alert
          onClose={handleClose}
          severity={severity}
          sx={{
            width: '100%',
            minWidth: 0,
            maxWidth: snackbarMaxWidth,
            boxSizing: 'border-box',
          }}
        >
          {message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};
